import _ from 'lodash'

import {
  getDeal,
  getDeals,
  getEventGroup,
  getMarkets,
  getPerformances,
  getSellableItem,
  getVenues,
} from 'selectors/entities'
import { createDeepEqualSelector } from 'utils/selectors'
import { getPrevHour, isAfter, startOfDay, toMoment } from 'utils/datetime'
import { PERFORMANCES_VIEW_CURRENT } from 'constants/eventgroup-page-constants'

export const PAST_PERFORMANCES_CUTOFF_HOUR = 4

export const getEventGroupVenue = _.memoize(eventGroupId => createDeepEqualSelector(
  [getEventGroup(eventGroupId), getVenues],
  (eventGroup, venues) => (eventGroup && venues ? venues[eventGroup.venue] : null)
))

export const getEventGroupMarket = _.memoize(eventGroupId => createDeepEqualSelector(
  [getEventGroup(eventGroupId), getMarkets],
  (eventGroup, markets) => (eventGroup && markets ? markets[eventGroup.market] : null)
))

export const getEventGroupPerformancesViewType = state => state.pages.eventGroupPage.performancesViewType

export const showCurrentPerformances = state =>
  state.pages.eventgroupPage.performancesViewType === PERFORMANCES_VIEW_CURRENT

export const transformTicketTypes = (performance, deals) => {
  const uniqueTicketTypes = _.uniq(performance.deals.map(dealId => deals[dealId].ticketType))
  if (uniqueTicketTypes.length > 2) {
    return `${uniqueTicketTypes[0]} + ${uniqueTicketTypes.length - 1} more`
  }
  return uniqueTicketTypes.join(', ')
}

export const groupPerformances = (past = false) => (eventGroup, performances, deals) => (eventGroup && performances ?
  _(eventGroup.performances)
    .mapValues(performanceId => ({ ...performances[performanceId] }))
    .filter((performance) => {
      const cutOff = getPrevHour(4)
      return past ? !isAfter(performance.startTime, cutOff) : isAfter(performance.startTime, cutOff)
    })
    .map(performance => ({
      ...performance,
      ticketType: transformTicketTypes(performance, deals),
    }))
    .groupBy(performance => startOfDay(performance.startTime))
    .mapValues(items => ({
      performances: _.orderBy(items, performance => toMoment(performance.startTime)),
      soldTickets: _.sumBy(items, 'soldTickets'),
      totalPartnerShare: _.sumBy(items, item => Number(item.totalPartnerShare)),
    }))
    .map((group, startTime) => ({ startTime, group }))
    .orderBy(group => toMoment(group.startTime), past ? 'desc' : 'asc')
    .value()
  : null)

export const getPerformanceGroups = _.memoize(eventGroupId => createDeepEqualSelector(
  [getEventGroup(eventGroupId), getPerformances, getDeals],
  groupPerformances()
))

export const getPastPerformanceGroups = _.memoize(eventGroupId => createDeepEqualSelector(
  [getEventGroup(eventGroupId), getPerformances, getDeals],
  groupPerformances(true)
))

export const getEventGroupPerformancesViewIndex = state => (
  showCurrentPerformances(state) ? 0 : 1
)

export const getEventGroupDeals = eventGroupId => state =>
  getEventGroup(eventGroupId)(state).deals.map(dealId => getDeal(dealId)(state))

export const getEventGroupSellableItems = eventGroupId => state =>
  getEventGroup(eventGroupId)(state).sellableItems.map(sellableItemId => getSellableItem(sellableItemId)(state))

export const getDraftEventGroup = state => state.draftEventGroup || {}

export const getChangedDraftEventGroup = (state) => {
  const draftEventGroup = getDraftEventGroup(state)
  if (!(draftEventGroup && _.has(draftEventGroup, 'id'))) {
    return {}
  }

  const eventGroup = getEventGroup(draftEventGroup.id)(state)
  const changedFields = {}
  Object.keys(draftEventGroup).forEach((prop) => {
    if (_.has(draftEventGroup, prop) &&
          _.has(eventGroup, prop) &&
          draftEventGroup[prop] !== eventGroup[prop]) {
      changedFields[prop] = draftEventGroup[prop]
    }
  })
  return _.isEmpty(changedFields) ? {} : Object.assign(changedFields, { id: eventGroup.id })
}

export const isEditable = state => !_.isEmpty(getDraftEventGroup(state))

export const isSaving = state => !!state.pages.eventgroupDetailsPage.isSaving



/** WEBPACK FOOTER **
 ** ./src/selectors/event-group/event-group-selectors.js
 **/